"use server";

import { v4 as uuidv4 } from "uuid";

import { auth } from "@/auth";
import { GRP_ROLE_ADMIN } from "@/lib/grp";
import { requireRole } from "@/lib/permissions";
import {
  addFacturationRuleSchema,
  deleteFacturationRuleSchema,
  editFacturationRuleSchema,
} from "@/lib/schemas/facturation-rules";
import { prisma } from "@/prisma";
import type { ActionResult } from "@/types/server-action";

export async function addFacturationRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = addFacturationRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const {
    brandNumericCode,
    brandAlphabeticCode,
    brandAlphabeticLabel,
    billingCode,
    serviceType,
    noticeItemLabel,
    articleNumber,
    unitCost,
  } = parsed.data;

  const existing = await prisma.facturationRule.findFirst({
    where: {
      deletedAt: null,
      brandNumericCode,
      brandAlphabeticCode,
      billingCode,
      serviceType,
      articleNumber,
    },
  });

  if (existing) {
    return {
      success: false,
      message:
        "Conflit : une règle active existe déjà pour ce couple (marque, code facturation, service, article).",
    };
  }

  await prisma.facturationRule.create({
    data: {
      brandNumericCode,
      brandAlphabeticCode,
      brandAlphabeticLabel,
      billingCode,
      serviceType,
      noticeItemLabel,
      articleNumber,
      unitCost,
      isActive: true,
      version: 1,
      versionGroupId: uuidv4(),
      versionNote: null,
      deletedAt: null,
    },
  });

  return { success: true };
}

export async function editFacturationRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = editFacturationRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const {
    id,
    brandNumericCode,
    brandAlphabeticCode,
    brandAlphabeticLabel,
    billingCode,
    serviceType,
    noticeItemLabel,
    articleNumber,
    unitCost,
    isActive,
    versionNote,
  } = parsed.data;

  const existing = await prisma.facturationRule.findFirst({
    where: { id, deletedAt: null },
  });

  if (!existing) {
    return { success: false, message: "Règle introuvable" };
  }

  const mappingChanged =
    existing.brandNumericCode !== brandNumericCode ||
    existing.brandAlphabeticCode !== brandAlphabeticCode ||
    existing.billingCode !== billingCode ||
    existing.serviceType !== serviceType ||
    existing.articleNumber !== articleNumber;

  if (mappingChanged) {
    const conflict = await prisma.facturationRule.findFirst({
      where: {
        deletedAt: null,
        brandNumericCode,
        brandAlphabeticCode,
        billingCode,
        serviceType,
        articleNumber,
      },
    });

    if (conflict) {
      return {
        success: false,
        message:
          "Conflit : une règle active existe déjà pour la clé cible (marque, code facturation, service, article).",
      };
    }
  }

  await prisma.$transaction([
    prisma.facturationRule.update({
      where: { id },
      data: { deletedAt: new Date(), isActive: false },
    }),
    prisma.facturationRule.create({
      data: {
        brandNumericCode,
        brandAlphabeticCode,
        brandAlphabeticLabel,
        billingCode,
        serviceType,
        noticeItemLabel,
        articleNumber,
        unitCost,
        isActive,
        version: existing.version + 1,
        versionGroupId: existing.versionGroupId,
        versionNote: versionNote ?? null,
        deletedAt: null,
      },
    }),
  ]);

  return { success: true };
}

export async function deleteFacturationRule(
  formData: unknown,
): Promise<ActionResult> {
  const session = await auth();
  const { authorized } = requireRole(session, [GRP_ROLE_ADMIN]);
  if (!session) return { success: false, message: "Unauthorized" };
  if (!authorized) return { success: false, message: "Forbidden" };

  const parsed = deleteFacturationRuleSchema.safeParse(formData);
  if (!parsed.success) {
    return { success: false, fieldErrors: parsed.error.flatten().fieldErrors };
  }

  const { id } = parsed.data;

  await prisma.facturationRule.update({
    where: { id },
    data: { deletedAt: new Date(), isActive: false },
  });

  return { success: true };
}
